import { Investment } from "@rentablo/api/apiv1";

export type InvestmentMetricKey = "today" | "purchase" | "total" | "dividends";
export type InvestmentTextField = "name" | "isin" | "wkn";
export type InvestmentSortField = InvestmentTextField | InvestmentMetricKey;

export const TEXT_SORT_FIELDS: InvestmentTextField[] = ["name", "isin", "wkn"];
export const DEFAULT_METRIC_ORDER: InvestmentMetricKey[] = [
  "today",
  "purchase",
  "total",
  "dividends",
];

export const isMetricKey = (value: unknown): value is InvestmentMetricKey =>
  typeof value === "string" &&
  (DEFAULT_METRIC_ORDER as string[]).includes(value);

export const isTextSortField = (value: unknown): value is InvestmentTextField =>
  typeof value === "string" && (TEXT_SORT_FIELDS as string[]).includes(value);

export const isSortField = (value: unknown): value is InvestmentSortField =>
  isMetricKey(value) || isTextSortField(value);

export const getSortValue = (
  investment: Investment,
  field: InvestmentSortField,
  isGlobalCurrency: boolean
): string | number | undefined => {
  switch (field) {
    case "name":
      return investment.standardisedName || investment.name;
    case "isin":
      return investment.isin;
    case "wkn":
      return investment.wkn;
    case "today":
      return investment.quote?.changePercentAbs;
    case "purchase":
      return isGlobalCurrency
        ? investment.purchaseValueAC
        : investment.purchaseValue;
    case "total":
      return isGlobalCurrency
        ? investment.returnOnInvestmentInPercentAC
        : investment.returnOnInvestmentInPercent;
    case "dividends":
      return isGlobalCurrency
        ? investment.receivedTotalDividendAC
        : investment.receivedTotalDividend;
  }
};

const isMissing = (value: unknown) =>
  value === undefined || value === null || value === "";

// Positions without a value for the sort field always go last, whatever the
// direction, so a descending sort never starts with a block of empty rows.
export const sortInvestments = (
  investments: Investment[],
  field: InvestmentSortField,
  direction: TableSortDirection,
  isGlobalCurrency = false
): Investment[] => {
  const sign = direction === "DESC" ? -1 : 1;

  return [...investments].sort((a, b) => {
    const left = getSortValue(a, field, isGlobalCurrency);
    const right = getSortValue(b, field, isGlobalCurrency);

    if (isMissing(left) && isMissing(right)) return 0;
    if (isMissing(left)) return 1;
    if (isMissing(right)) return -1;

    if (typeof left === "number" && typeof right === "number") {
      return sign * (left - right);
    }

    return (
      sign *
      String(left).localeCompare(String(right), "de", {
        sensitivity: "base",
        numeric: true,
      })
    );
  });
};

export const moveItem = <T>(items: T[], from: number, to: number): T[] => {
  if (
    from === to ||
    from < 0 ||
    to < 0 ||
    from >= items.length ||
    to >= items.length
  ) {
    return items;
  }

  const next = [...items];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
};
